import {AriaButtonOptions, useButton} from '@react-aria/button';
import React, {ElementType, useRef} from 'react';
import {useFocusRing} from "@react-aria/focus";
import {useHover} from "@react-aria/interactions";
import clsx from "clsx";
import {useAuth} from "../security/AuthProvider";

interface ButtonProps extends AriaButtonOptions<ElementType> {
    children?: React.ReactNode;
    variant?: 'outlined' | 'contained' | 'embedded';
    color?: 'primary' | 'secondary' | 'attention';
    size?: 'small' | 'normal';
    fullWidth?: boolean;
    className?: string;
    loading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    requiredPermissions?: string[];
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, forwardedRef) => {

    const {
        children,
        variant = 'contained',
        color = 'primary',
        size = 'normal',
        fullWidth = false,
        className,
        loading,
        startIcon,
        endIcon,
        requiredPermissions,
        isDisabled
    } = props;

    const {hasAnyAuthority} = useAuth();

    const internalRef = useRef<HTMLButtonElement | null>(null);

    // Use useImperativeHandle to sync the forwarded ref with our internal ref
    React.useImperativeHandle(forwardedRef, () => internalRef.current!);
    const {buttonProps, isPressed} = useButton(props, internalRef);
    const {isFocused, isFocusVisible, focusProps} = useFocusRing();
    const {hoverProps, isHovered} = useHover({});

    const variantClass = `btn-${variant}-${color}`;

    const sizeClass = `btn-size-${size}`;

    if (requiredPermissions && requiredPermissions.length > 0 && !hasAnyAuthority(requiredPermissions)) {
        return null;
    }

    return (
        <button {...buttonProps} {...hoverProps} {...focusProps} ref={internalRef}
                disabled={isDisabled}
                aria-busy={loading || undefined}
                className={clsx(
                    'btn-base',
                    variantClass,
                    {
                        'w-full': fullWidth,
                        'btn-hovered': isHovered && !isDisabled,
                        'btn-pressed': isPressed && !isDisabled,
                        'btn-focused': isFocused && !isDisabled,
                        'btn-focus-visible': isFocusVisible && !isDisabled,
                        'btn-disabled': isDisabled
                    },
                    sizeClass,
                    className
                )}>
            {loading ? (
                <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    {startIcon && <span className="icon-start">{startIcon}</span>}
                    {children}
                    {endIcon && <span className="icon-end">{endIcon}</span>}
                </div>
            )}
        </button>
    );
});
